import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { InsuranceReimbursement } from '../types';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  category_id: number | null;
  subcategory_id: number | null;
  is_transfer: boolean;
  confidence_score: number | null;
  linked_account_id: number;
  account_name: string;
  category_name?: string;
  subcategory_name?: string;
  category_color?: string;
  is_verified?: boolean;
  verification_date?: string;
  categorization_source?: 'manual' | 'rule' | 'system';
  categorized_by_rule_id?: number;
  original_amount?: number;
  reimbursed_amount?: number;
  reimbursement_date?: string;
  insurance_provider?: string;
  is_reimbursable?: boolean;
  is_business_expense?: boolean;
  business_category?: string;
  notes?: string;
  already_covered?: number;
}

interface InsuranceReimbursementModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onUpdate: (transaction: Transaction) => void;
}

export const InsuranceReimbursementModal: React.FC<InsuranceReimbursementModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onUpdate
}) => {
  const [potentialExpenses, setPotentialExpenses] = useState<Transaction[]>([]);
  const [existingReimbursements, setExistingReimbursements] = useState<InsuranceReimbursement[]>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<{ [expenseId: number]: number }>({});
  const [pendingDeletions, setPendingDeletions] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [descriptionFilter, setDescriptionFilter] = useState<string>('');
  const [amountFilter, setAmountFilter] = useState<string>('');

  useEffect(() => {
    if (isOpen && transaction && transaction.amount > 0) {
      loadData();
      // Reset filters and pending deletions when modal opens
      setDescriptionFilter('');
      setAmountFilter('');
      setPendingDeletions(new Set());
    }
  }, [isOpen, transaction]);

  const loadData = async () => {
    if (!transaction) return;

    try {
      setLoading(true);
      setError(null);

      const [potentialExpensesData, existingReimbursementsData] = await Promise.all([
        api.transactions.getPotentialExpenses(transaction.id),
        api.transactions.getInsuranceReimbursements(transaction.id)
      ]);

      setPotentialExpenses(potentialExpensesData);
      setExistingReimbursements(existingReimbursementsData);
    } catch (err) {
      console.error('Error loading insurance data:', err);
      setError('Failed to load insurance data');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseSelection = (expenseId: number, coveredAmount: number) => {
    setSelectedExpenses(prev => ({
      ...prev,
      [expenseId]: coveredAmount
    }));
  };

  const removeExpenseSelection = (expenseId: number) => {
    setSelectedExpenses(prev => {
      const newSelected = { ...prev };
      delete newSelected[expenseId];
      return newSelected;
    });
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    try {
      setSubmitting(true);
      setError(null);

      // Create reimbursement links for each selected expense
      const promises = Object.entries(selectedExpenses).map(([expenseId, coveredAmount]) =>
        api.transactions.createInsuranceReimbursement({
          income_transaction_id: transaction.id,
          expense_transaction_id: parseInt(expenseId),
          covered_amount: coveredAmount,
          notes: `Insurance reimbursement from ${transaction.description || transaction.merchant_name}`
        })
      );

      await Promise.all(promises);

      // Mark the transaction as insurance eligible if not already
      if (!transaction.is_reimbursable) {
        await api.transactions.markInsuranceEligible(transaction.id, true);
      }

      // Update local transaction state to reflect is_reimbursable flag
      // The parent component should reload transactions to get full data
      onUpdate({ ...transaction, is_reimbursable: true });

      onClose();
    } catch (err) {
      console.error('Error creating insurance reimbursements:', err);
      setError('Failed to create insurance reimbursements');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReimbursement = async (reimbursementId: number) => {
    try {
      await api.transactions.deleteInsuranceReimbursement(reimbursementId);
      await loadData(); // Reload data
    } catch (err) {
      console.error('Error deleting reimbursement:', err);
      setError('Failed to delete reimbursement');
    }
  };

  const handleUnmarkAsReimbursement = async () => {
    if (!transaction) return;

    // Confirm with user
    if (!window.confirm('Are you sure you want to unmark this transaction as a reimbursement? This cannot be undone.')) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Unmark the transaction as a reimbursement
      await api.transactions.markInsuranceEligible(transaction.id, false);

      // Close modal and refresh transactions
      onUpdate({ ...transaction, is_reimbursable: false });
      onClose();
    } catch (err) {
      console.error('Error unmarking reimbursement:', err);
      setError('Failed to unmark as reimbursement');
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalSelected = () => {
    return Object.values(selectedExpenses).reduce((sum, amount) => sum + amount, 0);
  };

  const getRemainingAmount = () => {
    if (!transaction) return 0;
    // Exclude pending deletions from calculation
    const totalAlreadyAllocated = existingReimbursements
      .filter(r => r.income_transaction_id === transaction.id && !pendingDeletions.has(r.id))
      .reduce((sum, r) => sum + r.covered_amount, 0);
    const totalSelected = getTotalSelected();
    return transaction.amount - totalAlreadyAllocated - totalSelected;
  };

  // Get visible reimbursements (excluding pending deletions)
  const visibleReimbursements = existingReimbursements.filter(r => !pendingDeletions.has(r.id));

  if (!isOpen || !transaction) return null;

  const isIncomeTransaction = transaction.amount > 0;

  if (!isIncomeTransaction) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Insurance Reimbursement</h2>
            <button onClick={onClose} className="close-button">×</button>
          </div>
          <div className="modal-body">
            <p>Insurance reimbursement tracking is only available for income transactions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content insurance-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Insurance Reimbursement Setup</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="modal-body">
          {/* Transaction Summary */}
          <div style={{
            background: 'var(--background-card, #1a1a1a)',
            border: '1px solid var(--border-color, #333)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #fff)' }}>Income Transaction</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color, #333)', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Date</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Description</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>Total</th>
                    <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>Remaining to Allocate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid var(--border-color, #333)' }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                      {formatDate(transaction.date)}
                    </td>
                    <td style={{ padding: '0.75rem', fontWeight: '500', color: 'var(--text-primary, #fff)' }}>
                      {transaction.merchant_name || transaction.description}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--success-color, #10B981)', fontWeight: '600' }}>
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '500', color: getRemainingAmount() > 0.01 ? 'var(--warning-color, #F59E0B)' : 'var(--text-primary, #fff)' }}>
                      {formatCurrency(getRemainingAmount())}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading-container dark-theme">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading expenses...</p>
            </div>
          ) : (
            <>
              {/* Existing Reimbursements */}
              {visibleReimbursements.length > 0 && (
                <div className="existing-reimbursements" style={{
                  background: 'var(--background-card, #1a1a1a)',
                  border: '1px solid var(--border-color, #333)',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary, #fff)' }}>Current Reimbursements</h3>
                  <div className="reimbursements-list" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color, #333)', textAlign: 'left' }}>
                          <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Date</th>
                          <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Description</th>
                          <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>Total</th>
                          <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>Covered</th>
                          <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>% Covered</th>
                          <th style={{ padding: '0.75rem', width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleReimbursements.map((reimbursement) => {
                          const expenseTotal = reimbursement.expense_amount ? Math.abs(reimbursement.expense_amount) : 0;
                          const coveredAmount = reimbursement.covered_amount || 0;
                          const percentCovered = expenseTotal > 0 ? ((coveredAmount / expenseTotal) * 100).toFixed(1) : '0.0';
                          
                          return (
                            <tr key={reimbursement.id} style={{ borderBottom: '1px solid var(--border-color, #333)' }}>
                              <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                                {reimbursement.expense_date ? formatDate(reimbursement.expense_date) : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', fontWeight: '500', color: 'var(--text-primary, #fff)' }}>
                                {reimbursement.expense_description || 'Unknown expense'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-primary, #fff)' }}>
                                {formatCurrency(expenseTotal)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '500', color: 'var(--success-color, #10B981)' }}>
                                {formatCurrency(coveredAmount)}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                                {percentCovered}%
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleDeleteReimbursement(reimbursement.id)}
                                  className="btn btn-danger btn-sm"
                                  title="Remove reimbursement link"
                                  disabled={submitting}
                                  style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Potential Expenses */}
              <div className="potential-expenses">
                <h3>Select Expenses to Cover</h3>
                
                {/* Filters */}
                {potentialExpenses.length > 0 && (
                  <div className="expense-filters" style={{ 
                    marginBottom: '1rem', 
                    padding: '0.75rem', 
                    background: 'var(--background-secondary, #1a1a1a)', 
                    borderRadius: '6px',
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                        Filter by Description
                      </label>
                      <input
                        type="text"
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                        placeholder="Search descriptions..."
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: 'var(--background-tertiary, #0a0a0a)',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          color: 'var(--text-primary, #fff)',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '150px' }}>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                        Filter by Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={amountFilter}
                        onChange={(e) => setAmountFilter(e.target.value)}
                        placeholder="Min amount..."
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          background: 'var(--background-tertiary, #0a0a0a)',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          color: 'var(--text-primary, #fff)',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {(() => {
                  // Filter expenses based on filters
                  let filteredExpenses = potentialExpenses;
                  
                  if (descriptionFilter.trim()) {
                    const filterLower = descriptionFilter.toLowerCase();
                    filteredExpenses = filteredExpenses.filter(expense => 
                      (expense.description?.toLowerCase().includes(filterLower) || 
                       expense.merchant_name?.toLowerCase().includes(filterLower))
                    );
                  }
                  
                  if (amountFilter.trim()) {
                    const minAmount = parseFloat(amountFilter);
                    if (!isNaN(minAmount)) {
                      filteredExpenses = filteredExpenses.filter(expense => 
                        Math.abs(expense.amount) >= minAmount
                      );
                    }
                  }
                  
                  if (filteredExpenses.length === 0) {
                    return (
                      <div className="empty-state">
                        <p>No expenses match the current filters. {potentialExpenses.length > 0 && 'Try adjusting your search criteria.'}</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="expenses-list">
                      {filteredExpenses.map((expense) => {
                        const maxCoverable = Math.abs(expense.amount) - (expense.already_covered || 0);
                        const currentlySelected = selectedExpenses[expense.id] || 0;
                        const remainingToAllocate = getRemainingAmount() + currentlySelected;
                        const maxAllowable = Math.min(maxCoverable, remainingToAllocate);

                        return (
                          <div key={expense.id} className="expense-item">
                            <div className="expense-details">
                              <div className="expense-header">
                                <span className="expense-date">
                                  {new Date(expense.date).toLocaleDateString()}
                                </span>
                                <span className="expense-description">
                                  {expense.merchant_name || expense.description}
                                </span>
                                {expense.category_name && (
                                  <span 
                                    className="expense-category"
                                    style={{
                                      fontSize: '0.75rem',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '4px',
                                      backgroundColor: expense.category_color || 'var(--background-secondary, #333)',
                                      color: 'var(--text-primary, #fff)',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {expense.category_name}
                                    {expense.subcategory_name && ` - ${expense.subcategory_name}`}
                                  </span>
                                )}
                                <span className="expense-amount">
                                  {formatCurrency(expense.amount)}
                                </span>
                              </div>
                              {expense.already_covered && expense.already_covered > 0 && (
                                <div className="already-covered">
                                  Already covered: {formatCurrency(expense.already_covered)}
                                </div>
                              )}
                            </div>
                            
                            <div className="coverage-input">
                              <label>
                                Amount to cover:
                                <input
                                  type="number"
                                  min="0"
                                  max={maxAllowable}
                                  step="0.01"
                                  value={selectedExpenses[expense.id] || ''}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    if (value > 0 && value <= maxAllowable) {
                                      handleExpenseSelection(expense.id, value);
                                    } else if (value === 0) {
                                      removeExpenseSelection(expense.id);
                                    }
                                  }}
                                  placeholder="0.00"
                                  className="amount-input"
                                />
                              </label>
                              <button
                                onClick={() => handleExpenseSelection(expense.id, maxAllowable)}
                                className="btn btn-secondary btn-sm"
                                disabled={maxAllowable <= 0}
                              >
                                Max ({formatCurrency(maxAllowable)})
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Selected Summary */}
              {Object.keys(selectedExpenses).length > 0 && (
                <div className="selected-summary">
                  <h4>Summary</h4>
                  <div className="summary-details">
                    <div className="summary-row">
                      <span>Total Income:</span>
                      <span>{formatCurrency(transaction.amount)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Total Selected:</span>
                      <span>{formatCurrency(getTotalSelected())}</span>
                    </div>
                    <div className="summary-row">
                      <span>Remaining:</span>
                      <span>{formatCurrency(getRemainingAmount())}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          {visibleReimbursements.length === 0 && transaction.is_reimbursable ? (
            <button
              onClick={handleUnmarkAsReimbursement}
              disabled={submitting}
              className="btn btn-danger"
            >
              {submitting ? 'Removing...' : 'Remove Reimbursement Category'}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={Object.keys(selectedExpenses).length === 0 || submitting || getRemainingAmount() < 0 || getRemainingAmount() > 0.01}
              className="btn btn-primary"
              title={getRemainingAmount() > 0.01 ? `You must allocate the full amount. Remaining: ${formatCurrency(getRemainingAmount())}` : ''}
            >
              {submitting ? 'Creating Links...' : 'Create Reimbursement Links'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
